import { useState, useEffect, useCallback, useMemo } from "react"
import { OPRADatabaseResponse, OPRAPreset, OPRAEQ, OPRAProduct, OPRAVendor } from "@/types/opra"

// Configuration for OPRA data source
const OPRA_CONFIG = {
  // Production URL hosted on Digital Ocean Spaces
  PRODUCTION_URL: "https://jds-core-releases.nyc3.digitaloceanspaces.com/opra/database_v1.jsonl",
}

interface OPRAFilters {
  brand?: string
  model?: string
  author?: string
}

interface UseOPRAResult {
  presets: OPRAPreset[]
  brands: Array<{ id: string; name: string }>
  models: Array<{ id: string; name: string; brandId: string }>
  authors: Array<{ name: string; count: number }>
  isLoading: boolean
  error: string | null
  filteredPresets: OPRAPreset[]
  applyFilters: (filters: OPRAFilters) => void
  clearFilters: () => void
  refreshData: () => Promise<void>
}

// Transform OPRA data into your app's preset format
const transformOPRAData = (
  eqs: OPRAEQ[],
  products: OPRAProduct[],
  vendors: OPRAVendor[]
): OPRAPreset[] => {
  const productMap = new Map(products.map((p) => [p.id, p]))
  const vendorMap = new Map(vendors.map((v) => [v.id, v]))

  return eqs.map((eq) => {
    const product = productMap.get(eq.data.product_id)
    const vendor = product ? vendorMap.get(product.data.vendor_id) : null

    return {
      id: eq.id,
      name: product ? product.data.name : "Unknown Product",
      author: eq.data.author,
      details: eq.data.details,
      type: eq.data.type, // EQ type (e.g., "parametric_eq")
      vendor: vendor ? vendor.data.name : "Unknown Brand",
      product: product ? product.data.name : "Unknown Product",
      productType: product?.data.type, // Product type (e.g., "headphones")
      productSubtype: product?.data.subtype, // Product subtype (e.g., "earbuds", "in_ear")
      config: eq.data.parameters,
      headphone: {
        brand: { name: vendor ? vendor.data.name : "Unknown Brand" },
        model: product ? product.data.name : "Unknown Product",
      },
      source: "opra" as const,
      artwork: product?.data.line_art_96x64_png,
    }
  })
}

// Parse NDJSON format used by OPRA
const parseNDJSON = (ndjsonText: string): OPRADatabaseResponse => {
  const lines = ndjsonText.trim().split("\n")
  const vendors: OPRAVendor[] = []
  const products: OPRAProduct[] = []
  const eqs: OPRAEQ[] = []

  lines.forEach((line) => {
    try {
      const item = JSON.parse(line)
      switch (item.type) {
        case "vendor":
          vendors.push({ id: item.id, data: item.data })
          break
        case "product":
          products.push({ id: item.id, data: item.data })
          break
        case "eq":
          eqs.push({ id: item.id, data: item.data })
          break
      }
    } catch (error) {
      console.warn("Failed to parse OPRA line:", line, error)
    }
  })

  return {
    vendors,
    products,
    eqs,
    lastModified: new Date().toISOString(),
  }
}

export const useOPRA = (): UseOPRAResult => {
  const [rawData, setRawData] = useState<OPRADatabaseResponse | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [filters, setFilters] = useState<OPRAFilters>({})

  // Transform raw data into presets
  const presets = useMemo(() => {
    if (!rawData) return []
    return transformOPRAData(rawData.eqs, rawData.products, rawData.vendors)
  }, [rawData])

  // Extract unique brands
  const brands = useMemo(() => {
    if (!rawData) return []
    return rawData.vendors.map((vendor) => ({
      id: vendor.id,
      name: vendor.data.name,
    }))
  }, [rawData])

  // Extract unique models for selected brand
  const models = useMemo(() => {
    if (!rawData) return []
    return rawData.products.map((product) => ({
      id: product.id,
      name: product.data.name,
      brandId: product.data.vendor_id,
    }))
  }, [rawData])

  // Extract authors with preset counts
  const authors = useMemo(() => {
    const authorCounts = new Map<string, number>()
    presets.forEach((preset) => {
      const count = authorCounts.get(preset.author) || 0
      authorCounts.set(preset.author, count + 1)
    })
    return Array.from(authorCounts.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
  }, [presets])

  // Apply filters to presets
  const filteredPresets = useMemo(() => {
    let filtered = presets

    if (filters.brand) {
      filtered = filtered.filter((preset) => preset.vendor === filters.brand)
    }

    if (filters.model) {
      filtered = filtered.filter((preset) => preset.product === filters.model)
    }

    if (filters.author) {
      filtered = filtered.filter((preset) => preset.author === filters.author)
    }

    return filtered
  }, [presets, filters])

  // Fetch OPRA data from Digital Ocean Spaces
  const fetchOPRAData = useCallback(async () => {
    setIsLoading(true)
    setError(null)

    try {
      // Fetch from Digital Ocean Spaces
      console.log("🔄 Fetching OPRA database...")
      const response = await fetch(OPRA_CONFIG.PRODUCTION_URL)
      if (!response.ok) {
        throw new Error(`Failed to fetch OPRA data: ${response.statusText}`)
      }
      
      const ndjsonText = await response.text()
      const parsedData = parseNDJSON(ndjsonText)

      console.log("✅ Successfully loaded OPRA database")
      console.log(`📊 OPRA database contains:`)
      console.log(`   📦 ${parsedData.vendors.length} vendors`)
      console.log(`   🎧 ${parsedData.products.length} products`) 
      console.log(`   ⚡ ${parsedData.eqs.length} EQ profiles`)

      setRawData(parsedData)
    } catch (err) {
      console.error("❌ Error fetching OPRA data:", err)
      setError(err instanceof Error ? err.message : "Failed to fetch OPRA data")
    } finally {
      setIsLoading(false)
    }
  }, [])

  // Apply filters function
  const applyFilters = useCallback((newFilters: OPRAFilters) => {
    setFilters(newFilters)
  }, [])

  // Clear filters function
  const clearFilters = useCallback(() => {
    setFilters({})
  }, [])

  // Refresh data function
  const refreshData = useCallback(async () => {
    await fetchOPRAData()
  }, [fetchOPRAData])

  // Load data on mount
  useEffect(() => {
    fetchOPRAData()
  }, [fetchOPRAData])

  return {
    presets,
    brands,
    models,
    authors,
    isLoading,
    error,
    filteredPresets,
    applyFilters,
    clearFilters,
    refreshData,
  }
} 